import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { createDiagnoseService } from '../services/diagnoseService'

/**
 * Custom hook to manage diagnostic messages based on user claims
 */
export function useDiagnose() {
  const { userClaims, userPermissions: authUserPermissions, isCustomerAdmin } = useAuth()
  const [diagnoseMessages, setDiagnoseMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [diagnoseService, setDiagnoseService] = useState(null)
  const [filterType, setFilterType] = useState('all')
  const [filterDevice, setFilterDevice] = useState('all')
  const [timeRange, setTimeRange] = useState(24) // hours

  // Get customer name from claims
  const customerName = userClaims?.customer || null

  // Device restriction info
  const isSuperAdmin = userClaims?.sadmin === true
  const restrictions = authUserPermissions?.device_restrictions
  // sadmin and customer_admin see all devices; only restrict other roles with explicit device_restrictions
  const isRestricted = !isSuperAdmin && !isCustomerAdmin && Array.isArray(restrictions) && restrictions.length > 0
  const deviceRestrictions = isRestricted ? restrictions : null

  // User permissions
  const userPermissions = {
    isAdmin: isSuperAdmin,
    hasCustomerAccess: !!userClaims?.customer,
    hasLayoutAccess: !!userClaims?.layout
  }

  // Initialize diagnose service
  useEffect(() => {
    if (customerName) {
      const service = createDiagnoseService(customerName)
      setDiagnoseService(service)
      
      return () => {
        if (service) {
          service.cleanup()
        }
      }
    } else {
      setDiagnoseService(null)
      setDiagnoseMessages([])
      setError('No customer access configured')
      setLoading(false)
    }
  }, [customerName])

  // Subscribe to diagnostic messages
  useEffect(() => {
    if (!diagnoseService) return

    setLoading(true)
    setError(null)

    let unsubscribe

    const applyRestrictions = (messages) => {
      if (!isRestricted) return messages
      return messages.filter(msg => deviceRestrictions.includes(msg.Device))
    }

    try {
      if (filterType !== 'all') {
        // Filter by type
        unsubscribe = diagnoseService.subscribeToDiagnoseByType(
          filterType,
          (messages) => {
            setDiagnoseMessages(applyRestrictions(messages))
            setLoading(false)
          }
        )
      } else if (filterDevice !== 'all') {
        // Filter by device
        unsubscribe = diagnoseService.subscribeToDeviceDiagnose(
          filterDevice,
          (messages) => {
            setDiagnoseMessages(applyRestrictions(messages))
            setLoading(false)
          }
        )
      } else {
        // All messages with time range
        unsubscribe = diagnoseService.subscribeToRecentDiagnose(
          timeRange,
          (messages) => {
            setDiagnoseMessages(applyRestrictions(messages))
            setLoading(false)
          }
        )
      }
    } catch (err) {
      console.error('Error subscribing to diagnose messages:', err)
      setError(err.message)
      setLoading(false)
    }

    return () => {
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [diagnoseService, filterType, filterDevice, timeRange, isRestricted, deviceRestrictions])

  // Helper functions
  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'invalid price':
        return 'fas fa-dollar-sign text-warning'
      case 'payment error':
        return 'fas fa-credit-card text-danger'
      case 'slot malfunction':
        return 'fas fa-exclamation-triangle text-danger'
      case 'low inventory':
        return 'fas fa-box text-warning'
      case 'system error':
        return 'fas fa-times-circle text-danger'
      case 'maintenance':
        return 'fas fa-wrench text-info'
      default:
        return 'fas fa-info-circle text-muted'
    }
  }

  const getTypeBadgeClass = (type) => {
    switch (type?.toLowerCase()) {
      case 'invalid price':
        return 'badge bg-warning text-dark'
      case 'payment error':
        return 'badge bg-danger'
      case 'slot malfunction':
        return 'badge bg-danger'
      case 'low inventory':
        return 'badge bg-warning text-dark'
      case 'system error':
        return 'badge bg-danger'
      case 'maintenance':
        return 'badge bg-info'
      default:
        return 'badge bg-light text-dark'
    }
  }

  const formatTimestamp = (dateTime) => {
    if (!dateTime) return 'N/A'
    
    try {
      const date = dateTime.toDate ? dateTime.toDate() : new Date(dateTime)
      return date.toLocaleString()
    } catch (error) {
      return 'Invalid date'
    }
  }

  const isExpired = (expires) => {
    if (!expires) return false
    try {
      const expireDate = expires.toDate ? expires.toDate() : new Date(expires)
      return expireDate < new Date()
    } catch (error) {
      return false
    }
  }

  const getMessageSummary = () => {
    const summary = {
      total: diagnoseMessages.length,
      'invalid price': 0,
      'payment error': 0,
      'slot malfunction': 0,
      'low inventory': 0,
      'system error': 0,
      'maintenance': 0,
      'expired': 0
    }

    diagnoseMessages.forEach(msg => {
      const type = msg.Type?.toLowerCase()
      if (summary.hasOwnProperty(type)) {
        summary[type]++
      }
      if (isExpired(msg.expires)) {
        summary.expired++
      }
    })

    return summary
  }

  return {
    diagnoseMessages,
    loading,
    error,
    customerName,
    userPermissions,
    filterType,
    setFilterType,
    filterDevice,
    setFilterDevice,
    timeRange,
    setTimeRange,
    getTypeIcon,
    getTypeBadgeClass,
    formatTimestamp,
    getMessageSummary,
    isExpired,
    deviceRestrictions,
    isRestricted
  }
}